/*
 * IMPORTS
 */
import React from 'react' // Npm: react.js library.
import PropTypes from 'prop-types' // Npm: react.js library.
import JoiBrowser from 'joi-browser' // Npm: Joi for frontend validation.
import _ from 'underscore' // Npm: Underscore.js library.
import { connect } from 'react-redux' // Npm: React Redux for state management.
import { useLazyQuery, useMutation, useQuery } from '@apollo/client' // Npm: Apollo client.
import { toast } from 'react-hot-toast' // Npm: React hot toast.
import { HiMinusCircle, HiPlusCircle } from 'react-icons/hi' // Npm: React icons.
import { HiBolt } from 'react-icons/hi2' // Npm: React icons.
import {
  Button,
  Checkbox,
  Box,
  Flex,
  Text,
  useBreakpointValue
} from '@chakra-ui/react' // Npm: Chakra UI components.



/*
 * PACKAGES
 */
import SubmitButton from 'components/SubmitButton'
import { MemoizedInput, MemoizedSelect } from 'components/MemoizedInput'


/*
 * GRAPHS
 */
import VendorAccountDeleteMutation from './__mutation__/index.vendorAccount.delete.mutation'
import VendorAccountReadQuery from './__query__/index.vendorAccount.read.query'
import RateReadQuery from './__query__/index.rate.read.query'
import VendorAccountUpdateMutation from './__mutation__/index.vendorAccount.update.mutation'
import VendorAccountCreateMutation from './__mutation__/index.vendorAccount.create.mutation'
import AttachRateMutation from './__mutation__/index.vendorAccount.attachRate.mutation'
import VendorAccountAttachSmppMutation from './__mutation__/index.vendorAccount.attachSmpp.mutation'
import SmppUpdateMutation from './__mutation__/index.smpp.update.mutation'
import SmppCreateMutation from './__mutation__/index.smpp.create.mutation'
import VendorAccountAddTlvMutation from './__mutation__/index.tlv.mutation' // tlv
import VendorAccountTlvQuery from './__query__/index.vendorAccount.tlv.query'
import DefaultReadTlvQuery from './__query__/index.default.read.tlv.query'


/*
 * OBJECTS
 */
const Index = ({
  isOpen,
  isCreateOnly,
  onClose,
  refetch,
  passOn
}) => {
  // Const assignment.
  const _sessionLimit = 20
  const _skipDifference = 10000

  // Hook assignment.
  const [rate, setRate] = React.useState('')
  const [tlvForms, setTlvForms] = React.useState([1])
  const [MutationAttachRate] = useMutation(AttachRateMutation)
  const [error, setError] = React.useState('')
  const [formVersion, setFormVersion] = React.useState(0)
  const [_renderVersion, _TriggerRender] = React.useReducer(j => j + 1, 0)
  const [MutationVendorAccountCreate, MutationVendorAccountCreateResponse] = useMutation(VendorAccountCreateMutation)
  const [MutationVendorAccountUpdate, MutationVendorAccountUpdateResponse] = useMutation(VendorAccountUpdateMutation)
  const [QueryVendorAccountRead, QueryVendorAccountReadResponse] = useLazyQuery(VendorAccountReadQuery, { 'fetchPolicy': Object.React.App.fetchPolicy })
  const [MutationVendorAccountDelete] = useMutation(VendorAccountDeleteMutation)
  const [MutationSmppCreate, MutationSmppCreateResponse] = useMutation(SmppCreateMutation)
  const [MutationSmppUpdate, MutationSmppUpdateResponse] = useMutation(SmppUpdateMutation)
  const [MutationAttachSmpp, MutationAttachSmppResponse] = useMutation(VendorAccountAttachSmppMutation)
  const [MutationVendorAccountAddTlv, MutationVendorAccountAddTlvResponse] = useMutation(VendorAccountAddTlvMutation)
  const [QueryVendorAccountTlv] = useLazyQuery(VendorAccountTlvQuery)
  const _RateReadVariables = React.useMemo(() => ({ 'type': 'TERMINATION', 'take': _skipDifference, 'skip': 0 }), [_skipDifference])
  const _RateReadQueryOptions = React.useMemo(() => ({ 'variables': _RateReadVariables, 'fetchPolicy': Object.React.App.fetchPolicy, 'pollInterval': 0, 'skip': !isOpen }), [_RateReadVariables, isOpen])
  const _DefaultReadTlvQueryOptions = React.useMemo(() => ({ 'fetchPolicy': Object.React.App.fetchPolicy, 'pollInterval': 0, 'skip': !isOpen }), [isOpen])
  const _RateReadQuery = useQuery(RateReadQuery, _RateReadQueryOptions)
  const _QueryDefaultReadTlv = useQuery(DefaultReadTlvQuery, _DefaultReadTlvQueryOptions)
  const _formDataRef = React.useRef({})
  const _tlvDataRef = React.useRef({})
  const _isCurrentViewMobile = useBreakpointValue({ 'base': false, 'md': false, 'lg': false, 'xl': false, 'sm': true, 'xs': true })
  const _ResetFormInputs = React.useCallback(() => setFormVersion(j => j + 1), [])

  // TLV Options from DefaultReadTlv query 
  const tlvOptions = React.useMemo(() => {
    return (_QueryDefaultReadTlv.data?.DefaultReadTlv || []).map(t => ({
      label: t.label,
      value: t.name
    }))
  }, [_QueryDefaultReadTlv.data?.DefaultReadTlv])

  // Create a map for label to name conversion
  const tlvLabelToName = React.useMemo(() => {
    const map = {}
    tlvOptions.forEach(opt => {
      map[opt.label] = opt.value
    })
    return map
  }, [tlvOptions])

  // Create a map for name to label conversion
  const tlvNameToLabel = React.useMemo(() => {
    const map = {}
    tlvOptions.forEach(opt => {
      map[opt.value] = opt.label
    })
    return map
  }, [tlvOptions])

  // Const assignment.
  const _sessionAllowedOptions = React.useMemo(() => Array.from({ 'length': _sessionLimit - 1 + 1 }, (_, i) => 1 + i), [])
  const _trunkTypeOptions = React.useMemo(() => Object.React.App.enums.TRUNK_TYPE.enums?.map(i => i.key), [])
  const _debuggingLevelOptions = React.useMemo(() => Object.React.App.enums.SMPP_DEBUGGING_LEVEL.enums?.map(i => i.key), [])
  const _interfaceVersionOptions = React.useMemo(() => Object.React.App.enums.INTERFACE_VERSION.enums?.map(i => i.key), [])
  const _tlvLabelOptions = React.useMemo(() => tlvOptions.map(opt => opt.label), [tlvOptions])
  const _rateOptions = React.useMemo(() => _RateReadQuery?.data?.RateRead?.map(({ id, displayName }) => `${displayName} (${id})`), [_RateReadQuery?.data?.RateRead])

  // Check if any TLV has hash checked.
  const hasAnyTlvHashChecked = React.useMemo(() => tlvForms.some((_, index) =>
    _tlvDataRef.current[`tlvIsHash__${index}`]
  ), [tlvForms, _renderVersion])
  const _CloseAfterMutation = React.useCallback(async () => {
    // Refetch listing data before closing when caller provides refresh hook.
    await refetch?.()

    // Close modal.
    return onClose?.()
  }, [onClose, refetch])

  // Object assignment.
  const _SubmitForm = async e => {
    // Prevent default behavior.
    e?.preventDefault()

    // Reset error.
    setError('')

    // Const assignment.
    const _tlvFilteredData = []

    // Validate TLV data if any TLV data exists
    if (!_.isEmpty(_tlvDataRef.current)) {
      // Const assignment.
      const _alreadyPushedData = []

      // Loop over _tlvDataRef and report failure if any key is empty.
      for (const [key, value] of Object.entries(_tlvDataRef.current)) {
        // Const assignment.
        const _keySplit = key.split?.('__')

        // If key is empty then report failure.
        if (key.includes('tlvTag') && _.isEmpty(_tlvDataRef?.current?.[`tlvValue__${_keySplit?.[1]}`])) return setError(` field tlvValue__${_keySplit?.[1]} at position ${_keySplit?.[1]} is empty.`)
        if (key.includes('tlvValue') && _.isEmpty(_tlvDataRef?.current?.[`tlvTag__${_keySplit?.[1]}`])) return setError(` field tlvTag__${_keySplit?.[1]} at position ${_keySplit?.[1]} is empty.`)

        // Only push data if it is not already pushed.
        if (!_alreadyPushedData.includes(_keySplit?.[1])) {
          // Push data to _tlvFilteredData.
          _tlvFilteredData.push({
            'name': _tlvDataRef?.current?.[`tlvTag__${_keySplit?.[1]}`], // Already stored as name
            'value': _tlvDataRef?.current?.[`tlvValue__${_keySplit?.[1]}`],
            'isHash': _tlvDataRef?.current?.[`tlvIsHash__${_keySplit?.[1]}`] || false
          })

          // Push data to _alreadyPushedData.
          _alreadyPushedData.push(_keySplit?.[1])
        }
      }

      // Validate TLV data.
      const _JoiSchemaTlv = JoiBrowser.array().items(JoiBrowser.object({
        'name': JoiBrowser.string().required(),
        'value': JoiBrowser.string().required(),
        'isHash': JoiBrowser.boolean().required()
      }))

      // Validate TLV.
      const _JoiSchemaTlvValidate = _JoiSchemaTlv.validate(_tlvFilteredData)

      // If error exists then report failure.
      if (_JoiSchemaTlvValidate.error) return setError(`TLV Tags: ${_JoiSchemaTlvValidate.error?.message}`)
    }

    // Const assignment.
    const _JoiSchema = JoiBrowser.object({
      'displayName': JoiBrowser.string().required(),
      // 'ip': JoiBrowser.string().max(45).ip({ 'version': ['ipv4', 'ipv6', 'ipvfuture'], 'cidr': 'optional' }).required(),
      'ip': JoiBrowser.alternatives().try(JoiBrowser.string().max(45).ip({ 'version': ['ipv4', 'ipv6', 'ipvfuture'], 'cidr': 'optional' }), JoiBrowser.string().max(253).regex(/^(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/, 'domain name')).required(),
      'username': JoiBrowser.string().required(),
      'password': JoiBrowser.string().required(),
      'txPort': JoiBrowser.number().required(),
      'rxPort': JoiBrowser.number().required(),
      'isSticky': JoiBrowser.boolean().optional(),
      'sessionTimeoutInterval': JoiBrowser.number().required(),
      'sessionAllowed': JoiBrowser.number().greater(0).less(11).optional(),
      'tps': JoiBrowser.number().greater(0).optional(),
      'enquireLinkInterval': JoiBrowser.number().required(),
      'maxReconnectAttempts': JoiBrowser.number().optional(),
      'reconnectBackoffFactor': JoiBrowser.number().optional(),
      'initialReconnectDelay': JoiBrowser.number().optional(),
      'sourceAddressTon': JoiBrowser.number().optional(),
      'sourceAddressNpi': JoiBrowser.number().optional(),
      'destinationAddressTon': JoiBrowser.number().optional(),
      'destinationAddressNpi': JoiBrowser.number().optional(),
      'trunkType': JoiBrowser.string().required(),
      'debuggingLevel': JoiBrowser.any().required(),
      'interfaceVersion': JoiBrowser.string().required(),
      'rate': JoiBrowser.string().optional(),
      'route': JoiBrowser.string().optional()
    }).options({ 'allowUnknown': true })

    // Remove empty values while preserving valid numeric zero values.
    _formDataRef.current = {
      ..._.pick(_formDataRef.current, value => 0 === value || Boolean(value)),
      'isSticky': _formDataRef.current?.isSticky
    }

    // Validate form data.
    const _JoiSchemaValidate = _JoiSchema.validate(_formDataRef.current)

    // If error exists then report failure.
    if (_JoiSchemaValidate.error) return setError(_JoiSchemaValidate.error?.message)

    // Create VendorAccount for given Vendor.
    const _MutationVendorAccount = await [isCreateOnly ? MutationVendorAccountCreate : MutationVendorAccountUpdate]?.[0]({
      'variables': {
        [isCreateOnly ? 'vendorId' : 'vendorAccountId']: isCreateOnly ? passOn?.vendorId : passOn?.vendorAccountId,
        'displayName': _formDataRef?.current?.displayName
      }
    })

    // If creating or updating vendor account caught an exception then report failure.
    if (_MutationVendorAccount instanceof Error) return _MutationVendorAccount

    // Execute update mutation.
    const _MutationSmpp = await [isCreateOnly ? MutationSmppCreate : MutationSmppUpdate]?.[0]({
      'variables': {
        'smppId': isCreateOnly || _.isEmpty(_MutationVendorAccount?.data?.VendorAccountUpdate?.Smpp) ? 'UN_KNOWN' : _MutationVendorAccount?.data?.VendorAccountUpdate?.Smpp?.id,
        'displayName': _formDataRef?.current?.displayName,
        'ip': _formDataRef?.current?.ip,
        'username': _formDataRef?.current?.username,
        'password': _formDataRef?.current?.password,
        'txPort': _formDataRef?.current?.txPort,
        'rxPort': _formDataRef?.current?.rxPort,
        'sessionTimeoutInterval': _formDataRef?.current?.sessionTimeoutInterval,
        'sessionAllowed': _formDataRef?.current?.sessionAllowed,
        'tps': _formDataRef?.current?.tps,
        'enquireLinkInterval': _formDataRef?.current?.enquireLinkInterval,
        'maxReconnectAttempts': _formDataRef?.current?.maxReconnectAttempts,
        'reconnectBackoffFactor': _formDataRef?.current?.reconnectBackoffFactor,
        'initialReconnectDelay': _formDataRef?.current?.initialReconnectDelay,
        'sourceAddressTon': _formDataRef?.current?.sourceAddressTon,
        'sourceAddressNpi': _formDataRef?.current?.sourceAddressNpi,
        'destinationAddressTon': _formDataRef?.current?.destinationAddressTon,
        'destinationAddressNpi': _formDataRef?.current?.destinationAddressNpi,
        'trunkType': _formDataRef?.current?.trunkType,
        'rate': _formDataRef?.current?.rate,
        'debuggingLevel': _formDataRef?.current?.debuggingLevel,
        'interfaceVersion': _formDataRef?.current?.interfaceVersion,
        'isSticky': _formDataRef?.current?.isSticky
      }
    })

    // If mutation caught an exception then report failure.
    if (_MutationSmpp instanceof Error) return _MutationSmpp
    if (isCreateOnly && 'HOST_DOWN_OR_UNREACHABLE' === _MutationSmpp?.data?.SmppCreate?.status) return MutationVendorAccountDelete({ 'variables': { 'vendorAccountId': _MutationVendorAccount?.data.VendorAccountCreate?.id } })

    // Object assignment.
    const _AttachRateAndRouteToVendor = async () => {
      // Local variable.
      let _MutationAttachRateToVendor

      // Only proceed if rate has data.
      if (!_.isEmpty(rate)) {
        // Local variable.
        let _rate

        // Variable assignment.
        _rate = rate

        // Only update rate if it is available.
        if (_rate?.includes('(') || _rate?.includes(')')) _rate = rate?.split('(')?.[1].split(')')?.[0]

        // Bind Rate to given vendor.
        _MutationAttachRateToVendor = await MutationAttachRate({
          'variables': {
            'vendorAccountId': _MutationVendorAccount?.data[isCreateOnly ? 'VendorAccountCreate' : 'VendorAccountUpdate']?.id,
            'rateId': _rate
          }
        })

        // If mutation caught an exception then report failure.
        if (_MutationAttachRateToVendor instanceof Error) {
          // Report failure.
          return _MutationAttachRateToVendor
        }

        // Style Guide.
        toast(_MutationAttachRateToVendor?.data?.VendorAccountAttachRate?.message)
      }

      // Report void.
      return void 0
    }

    // Object assignment - Add TLV Tags
    const _AttachTlvTags = async () => {
      // Only proceed if TLV has data
      if (0 < _tlvFilteredData.length) {
        // Add TLV tags to vendor account
        const _MutationAddTlv = await MutationVendorAccountAddTlv({
          'variables': {
            'vendorAccountId': _MutationVendorAccount?.data[isCreateOnly ? 'VendorAccountCreate' : 'VendorAccountUpdate']?.id,
            'tags': _tlvFilteredData
          }
        })

        // If mutation caught an exception then report failure.
        if (_MutationAddTlv instanceof Error) return _MutationAddTlv

        // Style Guide.
        toast(_MutationAddTlv?.data?.VendorAccountAddTlv?.message || 'TLV Tags added successfully')
      }

      // Report void.
      return void 0
    }

    // Style Guide.
    toast(_MutationSmpp?.data?.SmppCreate?.message ?? _MutationSmpp?.data?.SmppUpdate?.message)

    // Attach route and rate to given vendor.
    await _AttachRateAndRouteToVendor()

    // Attach TLV tags to given vendor.
    await _AttachTlvTags()

    /*
     * Only execute attach query if its about
     * creation of new smpp.
     */
    if ('CREATE_SUCCESSFUL' === _MutationSmpp?.data?.SmppCreate?.status) {
      // Attach given vendor with smpp.
      const _MutationAttachSmpp = await MutationAttachSmpp({ 'variables': { 'vendorAccountId': _MutationVendorAccount?.data[isCreateOnly ? 'VendorAccountCreate' : 'VendorAccountUpdate']?.id, 'smppId': _MutationSmpp?.data?.SmppCreate?.id } })

      // If mutation caught an exception then report failure.
      if (_MutationAttachSmpp instanceof Error) return _MutationAttachSmpp

      // Only close if attach is successful.
      if ('UPDATE_SUCCESSFUL' === _MutationAttachSmpp?.data?.VendorAccountAttachSmpp?.status) return _CloseAfterMutation()
    }

    // Only close if attach is successful.
    if ('UPDATE_SUCCESSFUL' === _MutationSmpp?.data?.SmppUpdate?.status) return _CloseAfterMutation()

    // Return void 0.
    return void 0
  }

  // Event handler.
  React.useEffect(() => {
    // Only prepare form data while modal is open.
    if (!isOpen) return

    // Reset form state for create mode.
    if (isCreateOnly) {
      _formDataRef.current = {
        sessionTimeoutInterval: 30000,
        enquireLinkInterval: 30000
      }
      _tlvDataRef.current = {}
      setRate('')
      setTlvForms([1])
      _ResetFormInputs()
      return
    }

    // Clear previous account values before loading update data.
    _formDataRef.current = {}
    _tlvDataRef.current = {}
    setRate('')
    setTlvForms([1])
    let _isActive = true

    // _Async handler.
    const _Async = async () => {
      // Const assignment.
      const _QueryVendorAccountReadQuery = await QueryVendorAccountRead({ 'variables': { 'vendorAccountId': isCreateOnly ? 'UN_KNOWN' : passOn?.vendorAccountId }, 'fetchPolicy': Object.React.App.fetchPolicy })

      // Report void if modal state has changed.
      if (!_isActive) return void 0

      // If query caught an exception then report failure.
      if (_QueryVendorAccountReadQuery instanceof Error) return _QueryVendorAccountReadQuery

      // Const assignment.
      const _vendorAccountRead = _.first(_QueryVendorAccountReadQuery?.data?.VendorAccountRead)

      /*
       * If details fetch complete then
       * update its value.
       */
      if (!_.isEmpty(_vendorAccountRead)) {
        // Update form data.
        _formDataRef.current = {
          'displayName': _vendorAccountRead?.Smpp?.displayName,
          'ip': _vendorAccountRead?.Smpp?.ip,
          'username': _vendorAccountRead?.Smpp?.username,
          'password': _vendorAccountRead?.Smpp?.password,
          'txPort': _vendorAccountRead?.Smpp?.txPort,
          'rxPort': _vendorAccountRead?.Smpp?.rxPort,
          'sessionTimeoutInterval': _vendorAccountRead?.Smpp?.sessionTimeoutInterval,
          'sessionAllowed': _vendorAccountRead?.Smpp?.sessionAllowed,
          'tps': _vendorAccountRead?.Smpp?.tps,
          'enquireLinkInterval': _vendorAccountRead?.Smpp?.enquireLinkInterval,
          'maxReconnectAttempts': _vendorAccountRead?.Smpp?.maxReconnectAttempts,
          'reconnectBackoffFactor': _vendorAccountRead?.Smpp?.reconnectBackoffFactor,
          'initialReconnectDelay': _vendorAccountRead?.Smpp?.initialReconnectDelay,
          'sourceAddressTon': _vendorAccountRead?.Smpp?.sourceAddressTon,
          'sourceAddressNpi': _vendorAccountRead?.Smpp?.sourceAddressNpi,
          'destinationAddressTon': _vendorAccountRead?.Smpp?.destinationAddressTon,
          'destinationAddressNpi': _vendorAccountRead?.Smpp?.destinationAddressNpi,
          'trunkType': _vendorAccountRead?.Smpp?.trunkType,
          'debuggingLevel': _vendorAccountRead?.Smpp?.debuggingLevel,
          'interfaceVersion': _vendorAccountRead?.Smpp?.interfaceVersion,
          'isSticky': _vendorAccountRead?.Smpp?.isSticky,
          'rate': _vendorAccountRead?.Rate?.displayName ? `${_vendorAccountRead?.Rate?.displayName} (${_vendorAccountRead?.Rate?.id})` : void 0
        }

        // Fetch TLV data if vendor account exists
        if (!isCreateOnly && passOn?.vendorAccountId) {
          const _QueryVendorAccountTlvData = await QueryVendorAccountTlv({ 'variables': { 'vendorAccountId': passOn?.vendorAccountId }, 'fetchPolicy': Object.React.App.fetchPolicy, })

          // Report void if modal state has changed.
          if (!_isActive) return void 0

          if (_QueryVendorAccountTlvData?.data?.VendorAccountTlv && 0 < _QueryVendorAccountTlvData?.data?.VendorAccountTlv.length) {
            // Update TLV forms count
            setTlvForms(Array.from({ 'length': _QueryVendorAccountTlvData?.data?.VendorAccountTlv.length }))

            // Populate TLV data
            _QueryVendorAccountTlvData?.data?.VendorAccountTlv.forEach((tlv, index) => {
              _tlvDataRef.current[`tlvTag__${index}`] = tlv?.name // Store only name for backend
              _tlvDataRef.current[`tlvValue__${index}`] = tlv?.value
              _tlvDataRef.current[`tlvIsHash__${index}`] = tlv?.isHash || false
            })

          }
        }

        // Update form inputs with fetched values.
        return _ResetFormInputs()
      }

      // Report failure.
      return void 0
    }; _Async()

    // Cleanup handler.
    return () => {
      _isActive = false
    }
  }, [QueryVendorAccountRead, QueryVendorAccountTlv, _ResetFormInputs, isCreateOnly, isOpen, passOn?.vendorAccountId])

  // Const assignment.
  const _isLoading = MutationVendorAccountCreateResponse.loading || MutationVendorAccountUpdateResponse.loading || MutationSmppCreateResponse.loading || MutationSmppUpdateResponse.loading || MutationAttachSmppResponse.loading || MutationVendorAccountAddTlvResponse.loading
  const _isInputDisabled = isCreateOnly ? false : _isLoading || QueryVendorAccountReadResponse.loading

  // Return component.
  return (
    <form onSubmit={_SubmitForm} key={formVersion}>
      <Flex w='100%' gap='22px' flexDir='column'>
        <Flex w='100%' gap='22px' flexDir={{ 'base': 'column', 'md': 'row' }}>
          <MemoizedInput
            disabled={_isInputDisabled}
            isRequired={true}
            name='displayName'
            label='Display name'
            placeholder='e.g. "Rocking Smpp"'
            onChange={({ target }) => {
              // Over spreading.
              const { name, value } = target

              // Update form data.
              _formDataRef.current = {
                ..._formDataRef?.current,
                [name]: value
              }
            }}
            isInvalid={error?.includes('displayName')}
            error={error}
            data={_formDataRef?.current?.displayName}
          />
          <MemoizedInput
            disabled={_isInputDisabled}
            name='ip'
            label='ip'
            placeholder='e.g. "0.0.0.0"'
            onChange={({ target }) => {
              // Over spreading.
              const { name, value } = target

              // Update form data.
              _formDataRef.current = {
                ..._formDataRef?.current,
                [name]: value
              }
            }}
            isRequired={true}
            error={error}
            isInvalid={error?.includes('ip')}
            data={_formDataRef?.current?.ip}
          />
        </Flex>
        <Flex w='100%' gap='22px' flexDir={{ 'base': 'column', 'md': 'row' }}>
          <MemoizedInput
            disabled={_isInputDisabled}
            name='username'
            label='SystemId'
            placeholder='e.g. "RockSmpp"'
            onChange={({ target }) => {
              // Over spreading.
              const { name, value } = target

              // Update form data.
              _formDataRef.current = {
                ..._formDataRef?.current,
                [name]: value
              }
            }}
            isRequired={true}
            error={error}
            isInvalid={error?.includes('username')}
            data={_formDataRef?.current?.username}
          />
          <MemoizedInput
            disabled={_isInputDisabled}
            name='password'
            label='Password'
            placeholder='e.g. "*******"'
            onChange={({ target }) => {
              // Over spreading.
              const { name, value } = target

              // Update form data.
              _formDataRef.current = {
                ..._formDataRef?.current,
                [name]: value
              }
            }}
            isRequired={true}
            error={error}
            isInvalid={error?.includes('password')}
            data={_formDataRef?.current?.password}
          />
        </Flex>
        <Flex w='100%' gap='22px' flexDir={{ 'base': 'column', 'md': 'row' }}>
          <MemoizedInput
            disabled={_isInputDisabled}
            name='txPort'
            label='Transmitter Port'
            placeholder='e.g. "2775"'
            onChange={({ target }) => {
              // Over spreading.
              const { name, value } = target

              // Update form data.
              _formDataRef.current = {
                ..._formDataRef?.current,
                [name]: Number(value)
              }
            }}
            isRequired={true}
            error={error}
            isInvalid={error?.includes('txPort')}
            data={_formDataRef?.current?.txPort}
          />
          <MemoizedInput
            disabled={_isInputDisabled}
            name='rxPort'
            label='Receiver Port'
            placeholder='e.g. "2775"'
            onChange={({ target }) => {
              // Over spreading.
              const { name, value } = target

              // Update form data.
              _formDataRef.current = {
                ..._formDataRef?.current,
                [name]: Number(value)
              }
            }}
            isRequired={true}
            error={error}
            isInvalid={error?.includes('rxPort')}
            data={_formDataRef?.current?.rxPort}
          />
        </Flex>
        <Flex w='100%' gap='22px' flexDir={{ 'base': 'column', 'md': 'row' }}>
          <MemoizedInput
            disabled={_isInputDisabled}
            isRequired={true}
            name='sessionTimeoutInterval'
            label='Response Time ( in Seconds )'
            placeholder='e.g. "3 is 3Seconds"'
            onChange={({ target }) => {
              // Over spreading.
              const { name, value } = target

              // Make sure that value is greater then 0.
              if (0 >= Number(value)) return setError('sessionTimeoutInterval must be greater then 0')

              // Update form data.
              _formDataRef.current = {
                ..._formDataRef?.current,
                [name]: Number(value) * 1000
              }

              // Report void.
              return void 0
            }}
            error={error}
            isInvalid={error?.includes('sessionTimeoutInterval')}
            data={0 < _formDataRef?.current?.sessionTimeoutInterval ? _formDataRef?.current?.sessionTimeoutInterval / 1000 : _formDataRef?.current?.sessionTimeoutInterval}
          />
          <MemoizedSelect
            disabled={_isInputDisabled}
            name='sessionAllowed'
            label='Session Allowed'
            placeholder='e.g. "2"'
            onChange={({ target }) => {
              // Over spreading.
              const { name, value } = target

              // Update form data.
              _formDataRef.current = {
                ..._formDataRef?.current,
                [name]: Number(value)
              }
            }}
            error={error}
            options={_sessionAllowedOptions}
            isInvalid={error?.includes('sessionAllowed')}
            data={_formDataRef?.current?.sessionAllowed}
          />
        </Flex>
        <Flex w='100%' gap='22px' flexDir={{ 'base': 'column', 'md': 'row' }}>
          <MemoizedInput
            disabled={_isInputDisabled}
            name='tps'
            label='Submit Speed Per Second'
            placeholder='e.g. "1000"'
            onChange={({ target }) => {
              // Over spreading.
              const { name, value } = target

              // Update form data.
              _formDataRef.current = {
                ..._formDataRef?.current,
                [name]: 1000 < Number(value ?? 0) ? 1000 : Number(value ?? 0)
              }
            }}
            error={error}
            isInvalid={error?.includes('tps')}
            data={_formDataRef?.current?.tps}
          />
          <MemoizedInput
            disabled={_isInputDisabled}
            isRequired={true}
            name='enquireLinkInterval'
            label='Enquire link ( in Seconds )'
            placeholder='e.g. "60 is 60Seconds"'
            onChange={({ target }) => {
              // Over spreading.
              const { name, value } = target

              // Make sure that value is greater then 0.
              if (0 >= Number(value)) return setError('enquireLinkInterval must be greater then 0')

              // Update form data.
              _formDataRef.current = {
                ..._formDataRef?.current,
                [name]: Number(value ?? 0) * 1000
              }

              // Report void.
              return void 0
            }}
            error={error}
            isInvalid={error?.includes('enquireLinkInterval')}
            data={1000 < _formDataRef?.current?.enquireLinkInterval ? _formDataRef?.current?.enquireLinkInterval / 1000 : _formDataRef?.current?.enquireLinkInterval}
          />
        </Flex>
        <Flex w='100%' gap='22px' flexDir={{ 'base': 'column', 'md': 'row' }}>
          <MemoizedInput
            disabled={_isInputDisabled}
            name='maxReconnectAttempts'
            label='Max Reconnect'
            placeholder='e.g. "0"'
            onChange={({ target }) => {
              // Over spreading.
              const { name, value } = target

              // Update form data.
              _formDataRef.current = {
                ..._formDataRef?.current,
                [name]: Number(value)
              }
            }}
            error={error}
            isInvalid={error?.includes('maxReconnectAttempts')}
            data={_formDataRef?.current?.maxReconnectAttempts}
          />
          <MemoizedInput
            disabled={_isInputDisabled}
            name='reconnectBackoffFactor'
            label='RD ( in Seconds )'
            placeholder='e.g. "2 is 2Seconds"'
            onChange={({ target }) => {
              // Over spreading.
              const { name, value } = target

              // Make sure that value is greater then 0.
              if (0 >= Number(value)) return setError('reconnectBackoffFactor must be greater then 0')

              // Update form data.
              _formDataRef.current = {
                ..._formDataRef?.current,
                [name]: Number(value) * 1000
              }

              // Report void.
              return void 0
            }}
            error={error}
            isInvalid={error?.includes('reconnectBackoffFactor')}
            data={0 < _formDataRef?.current?.reconnectBackoffFactor ? _formDataRef?.current?.reconnectBackoffFactor / 1000 : _formDataRef?.current?.reconnectBackoffFactor}
          />
          <MemoizedInput
            disabled={_isInputDisabled}
            name='initialReconnectDelay'
            label='IRD ( in Seconds )'
            placeholder='e.g. "1 is 1Second"'
            onChange={({ target }) => {
              // Over spreading.
              const { name, value } = target

              // Make sure that value is greater then 0.
              if (0 >= Number(value)) return setError('initialReconnectDelay must be greater then 0')

              // Update form data.
              _formDataRef.current = {
                ..._formDataRef?.current,
                [name]: Number(value)
              }

              // Report void.
              return void 0
            }}
            error={error}
            isInvalid={error?.includes('initialReconnectDelay')}
            data={0 < _formDataRef?.current?.initialReconnectDelay ? _formDataRef?.current?.initialReconnectDelay / 1000 : _formDataRef?.current?.initialReconnectDelay}
          />
        </Flex>
        <Flex w='100%' gap='22px' flexDir={{ 'base': 'column', 'md': 'row' }}>
          <MemoizedInput
            disabled={_isInputDisabled}
            name='sourceAddressTon'
            label='SourceAddress ton'
            placeholder='e.g. "0"'
            onChange={({ target }) => {
              // Over spreading.
              const { name, value } = target

              // Update form data.
              _formDataRef.current = {
                ..._formDataRef?.current,
                [name]: Number(value)
              }
            }}
            error={error}
            isInvalid={error?.includes('sourceAddressTon')}
            data={_formDataRef?.current?.sourceAddressTon}
          />
          <MemoizedInput
            disabled={_isInputDisabled}
            name='sourceAddressNpi'
            label='SourceAddress npi'
            placeholder='e.g. "0"'
            onChange={({ target }) => {
              // Over spreading.
              const { name, value } = target

              // Update form data.
              _formDataRef.current = {
                ..._formDataRef?.current,
                [name]: Number(value)
              }
            }}
            error={error}
            isInvalid={error?.includes('sourceAddressNpi')}
            data={_formDataRef?.current?.sourceAddressNpi}
          />
        </Flex>
        <Flex w='100%' gap='22px' flexDir={{ 'base': 'column', 'md': 'row' }}>
          <MemoizedInput
            disabled={_isInputDisabled}
            name='destinationAddressTon'
            label='DestinationAddress Ton'
            placeholder='e.g. "1"'
            onChange={({ target }) => {
              // Over spreading.
              const { name, value } = target

              // Update form data.
              _formDataRef.current = {
                ..._formDataRef?.current,
                [name]: Number(value)
              }
            }}
            error={error}
            isInvalid={error?.includes('destinationAddressTon')}
            data={_formDataRef?.current?.destinationAddressTon}
          />
          <MemoizedInput
            disabled={_isInputDisabled}
            name='destinationAddressNpi'
            label='DestinationAddress npi'
            placeholder='e.g. "1"'
            onChange={({ target }) => {
              // Over spreading.
              const { name, value } = target

              // Update form data.
              _formDataRef.current = {
                ..._formDataRef?.current,
                [name]: Number(value)
              }
            }}
            error={error}
            isInvalid={error?.includes('destinationAddressNpi')}
            data={_formDataRef?.current?.destinationAddressNpi}
          />
        </Flex>
        <Flex w='100%' flexDir='column' gap='22px'>
          <Flex w='100%' gap='22px' flexDir={{ 'base': 'column', 'md': 'row' }}>
            <MemoizedSelect
              disabled={_isInputDisabled}
              isRequired={true}
              name='trunkType'
              label='Route'
              onChange={({ target }) => {
                // Over spreading.
                const { name, value } = target

                // Update form data.
                _formDataRef.current = {
                  ..._formDataRef?.current,
                  [name]: value
                }
              }}
              error={error}
              placeholder='e.g. "DIRECT"'
              options={_trunkTypeOptions}
              isInvalid={error?.includes('trunkType')}
              data={_formDataRef?.current?.trunkType}
            />
            <MemoizedSelect
              disabled={_isInputDisabled}
              isRequired={true}
              name='debuggingLevel'
              label='Error Code Level'
              placeholder='e.g. "ERROR"'
              onChange={({ target }) => {
                // Over spreading.
                const { name, value } = target

                // Update form data.
                _formDataRef.current = {
                  ..._formDataRef?.current,
                  [name]: value
                }
              }}
              error={error}
              options={_debuggingLevelOptions}
              isInvalid={error?.includes('debuggingLevel')}
              data={_formDataRef?.current?.debuggingLevel}
            />
            <MemoizedSelect
              disabled={_isInputDisabled}
              isRequired={true}
              name='interfaceVersion'
              label='Interface Version'
              placeholder='e.g. "3.4"'
              onChange={({ target }) => {
                const { name, value } = target

                _formDataRef.current = {
                  ..._formDataRef?.current,
                  [name]: value
                }
              }}
              error={error}
              options={_interfaceVersionOptions}
              isInvalid={error?.includes('interfaceVersion')}
              data={_formDataRef?.current?.interfaceVersion}
            />
          </Flex>

          {/* TLV Tags Section, Conditional rendering based on hash checked */}
          <Flex
            w='100%'
            mt='8px'
            flexDir='column'
            gap='12px'
            {...(hasAnyTlvHashChecked && {
              p: '22px',
              bg: 'gray.50',
              border: '1px solid',
              borderColor: 'brand.500',
              borderRadius: 16,
              boxShadow: '14px 17px 40px 4px rgba(112, 144, 176, 0.2)'
            })}>

            {/*  Only show when hash is checked */}
            {hasAnyTlvHashChecked && (
              <>
                <Flex flexDir='row' alignItems='center' gap='6px' color='brand.500'>
                  <HiBolt />
                  <Text fontSize='lg' fontWeight={700} color='brand.500'>
                    TLV Configuration
                  </Text>
                </Flex>
                <Text fontSize='md' mb='12px' w='80%'>
                  TLV (Tag-Length-Value) tags allow you to send additional parameters with SMPP messages.
                </Text>
              </>
            )}

            {/* TLV Forms */}
            {tlvForms?.map((___, __index) => (
              <Flex
                flexDir='column'
                gap='12px'
                key={`tlv-${__index}`}>
                <Flex
                  flexDir={_isCurrentViewMobile ? 'column' : 'row'}
                  alignItems='flex-end'
                  gap='12px'>
                  <Flex
                    w='100%'
                    gap='12px'
                    flexDir={_isCurrentViewMobile ? 'column' : 'row'}>

                    {/* TLV Tag Select */}
                    <MemoizedSelect
                      disabled={_isInputDisabled}
                      name={`tlvTag__${__index}`}
                      label='TLV Tag'
                      placeholder='e.g. "Select Tag"'
                      onChange={({ target }) => {
                        const { value } = target
                        const tlvName = tlvLabelToName[value] || value
                        _tlvDataRef.current = {
                          ..._tlvDataRef.current,
                          [`tlvTag__${__index}`]: tlvName
                        }
                        _TriggerRender()
                      }}
                      error={error}
                      isInvalid={
                        error?.includes(`position ${__index}`) &&
                        error?.includes(`tlvTag__${__index}`)
                      }
                      options={_tlvLabelOptions}
                      data={
                        _tlvDataRef.current[`tlvTag__${__index}`]
                          ? tlvNameToLabel[_tlvDataRef.current[`tlvTag__${__index}`]]
                          : undefined
                      }
                    />

                    {/* TLV Value Input */}
                    <MemoizedInput
                      w='100%'
                      disabled={_isInputDisabled}
                      name={`tlvValue__${__index}`}
                      label='TLV Value'
                      placeholder='e.g. "Value123"'
                      error={error}
                      onChange={({ target }) => {
                        const { value } = target
                        _tlvDataRef.current = {
                          ..._tlvDataRef.current,
                          [`tlvValue__${__index}`]: value
                        }
                      }}
                      isInvalid={
                        error?.includes(`position ${__index}`) &&
                        error?.includes('Value')
                      }
                      data={_tlvDataRef.current[`tlvValue__${__index}`]}
                    />

                    {/* Hash Checkbox */}
                    <Flex alignItems='center' gap='4px' mt='25px'>
                      <Checkbox
                        disabled={_isInputDisabled}
                        colorScheme={
                          _tlvDataRef.current[`tlvIsHash__${__index}`] ? 'brand' : 'gray'
                        }
                        isChecked={_tlvDataRef.current[`tlvIsHash__${__index}`] || false}
                        onChange={({ target }) => {
                          _tlvDataRef.current = {
                            ..._tlvDataRef.current,
                            [`tlvIsHash__${__index}`]: target.checked
                          }
                          _TriggerRender()
                        }}
                      >
                        <Text
                          fontSize='sm'
                          fontWeight={
                            _tlvDataRef.current[`tlvIsHash__${__index}`] ? 600 : 500
                          }
                          color={
                            _tlvDataRef.current[`tlvIsHash__${__index}`]
                              ? 'brand.600'
                              : 'gray.600'
                          }
                        >
                          Hash?
                        </Text>
                      </Checkbox>
                    </Flex>
                  </Flex>

                  {/* Add/Remove Button */}
                  <Button
                    disabled={_isInputDisabled}
                    variant='basic'
                    type='button'
                    h='40px'
                    minH='40px'
                    mt={_isCurrentViewMobile ? '-40px' : '0'}
                    bg={__index === (tlvForms.length - 1) ? 'brand.400' : 'red.400'}

                    onClick={() => {
                      if (__index !== (tlvForms.length - 1)) {

                        const temp = {}
                        let newIndex = 0

                        tlvForms.forEach((_, i) => {
                          if (i !== __index) {
                            temp[`tlvTag__${newIndex}`] = _tlvDataRef.current[`tlvTag__${i}`]
                            temp[`tlvValue__${newIndex}`] = _tlvDataRef.current[`tlvValue__${i}`]
                            temp[`tlvIsHash__${newIndex}`] = _tlvDataRef.current[`tlvIsHash__${i}`]
                            newIndex++
                          }
                        })
                        _tlvDataRef.current = temp
                        setTlvForms(tlvForms.filter((u, i) => i !== __index))
                        _TriggerRender()
                        return
                      }
                      // Add new form
                      setTlvForms([...tlvForms, 1])
                    }}
                    _hover={{
                      bg: __index === (tlvForms.length - 1) ? 'brand.500' : 'red.500'
                    }}
                    _active={{
                      bg: __index === (tlvForms.length - 1) ? 'brand.600' : 'red.600'
                    }}
                    minW='50px'>
                    {__index === (tlvForms.length - 1)
                      ? <HiPlusCircle size={16} />
                      : <HiMinusCircle size={16} />
                    }
                  </Button>
                </Flex>
              </Flex>
            ))}
          </Flex>
          <MemoizedSelect
            disabled={_isInputDisabled}
            label='Assign Rate'
            data={_formDataRef?.current?.rate}
            placeholder='e.g. "Awesome Rate"'
            onChange={({ target }) => {
              // Over spreading.
              const { name, value } = target

              // Update form data.
              _formDataRef.current = {
                ..._formDataRef?.current,
                [name]: value
              }

              // Update rate.
              setRate(value)
            }}
            options={_rateOptions}
          />
        </Flex>
      </Flex>
      <Flex
        w='100%'
        mt='22px'
        color='rgba(43, 54, 116, 1)'
        fontWeight='700'
        fontSize='20px'
        lineHeight='24px'
        fontFamily='DM Sans'
        justifyContent='space-between'
        flexDir={_isCurrentViewMobile ? 'column' : 'row'}
        gap='22px'>
        <Flex flexDir='column'>
          <Flex>
            <Checkbox
              disabled={_isInputDisabled}
              colorScheme='red'
              color='red.500'
              name='isSticky'
              isChecked={_formDataRef?.current?.isSticky}
              onChange={({ target }) => {
                // Over spreading.
                const { name, checked } = target

                // Update form data.
                _formDataRef.current = {
                  ..._formDataRef?.current,
                  [name]: checked
                }

                // Update state.
                _TriggerRender()
              }}>
              Sticky Session ?
            </Checkbox>
          </Flex>
          <Text fontSize='14px' fontWeight='500' color='gray.500'>Do connection reconnect if <span style={{ 'fontWeight': 900 }}>SMPP</span> gets <span style={{ 'fontWeight': 900 }}>Closed</span> due to some reason.</Text>
        </Flex>
        <SubmitButton
          mt={0}
          disabled={_isInputDisabled}
          onSubmit={_SubmitForm}
          defaultText={isCreateOnly ? 'Create Smpp' : 'Update Smpp'}
          isLoading={_isLoading} />
      </Flex>
    </form>
  )
}


/*
 * PROPTYPES
 */
Index.propTypes = {
  'onClose': PropTypes.func,
  'refetch': PropTypes.func,
  'passOn': PropTypes.object,
  'isOpen': PropTypes.bool,
  'isCreateOnly': PropTypes.bool
}
Index.defaultProps = {}


/*
 * REDUX
 */
const _MapStateToProps = __state => ({ 'passOn': __state.PassOn })


/*
 * EXPORT
 */
export default connect(_MapStateToProps)(React.memo(Index))
